import { graphql } from '@/__generated__';
import { TestAppliedFilter } from '@/__generated__/graphql';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';
import { truthy } from '@/util/array';

export type TestAppliedListNode = NonNullable<
  ReturnType<typeof useDiscoveryAppliedTests>['data']
>[number];

// https://github.com/dbt-labs/codex-api/blob/main/schema.graphql
// https://github.com/dbt-labs/codex-api/blob/main/frontend/package.json#L33
// https://github.com/dbt-labs/codex-api/blob/main/frontend/codegen.yml
// generates: https://github.com/dbt-labs/codex-api/blob/main/frontend/src/typings/codex.ts
export const GetTestListByUniqueId = graphql(/* GraphQL */ `
  query AppliedTests(
    $first: Int!
    $after: String
    $environmentId: BigInt!
    $userId: BigInt
    $filter: TestAppliedFilter
  ) {
    environment(id: $environmentId, userId: $userId) {
      applied {
        lastUpdatedAt
        tests(first: $first, after: $after, filter: $filter) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              uniqueId
              name
              columnName
              testType
              model
              lastKnownResult
              executionInfo {
                lastRunStatus
                lastRunError
                executeCompletedAt
              }
              testMetadata {
                name
                kwargs
              }
            }
          }
        }
      }
    }
  }
`);

type TestByEnvironmentParams = {
  first: number;
  after?: string;
  environmentId: number | undefined;
  userId: number | null;
  filter?: Partial<TestAppliedFilter>;
  pause?: boolean;
};

export const useDiscoveryAppliedTests = ({
  first,
  after,
  environmentId,
  userId,
  filter,
  pause,
}: TestByEnvironmentParams) => {
  const query = usePaginatedQuery(
    {
      query: GetTestListByUniqueId,
      variables: { first, after, environmentId, userId, filter },
      pause: pause || !environmentId,
    },
    (data) => {
      const tests = data.environment.applied?.tests;
      const pageInfo = tests?.pageInfo;
      if (tests == null || pageInfo == null) return undefined;
      return {
        totalCount: tests.totalCount ?? null,
        endCursor: pageInfo.endCursor ?? null,
        hasNextPage: pageInfo.hasNextPage,
      };
    },
    (data) => {
      return (data.environment.applied?.tests.edges || [])
        .map((edge) => edge.node)
        .filter(truthy);
    },
    (data) => data.environment.applied?.lastUpdatedAt,
  );

  useCacheMonitor({ applied: query.lastUpdatedAt });

  return query;
};
